import { devlog, assetUrl } from '../../logic/utils.ts';
import { DeprecatedEl } from '../../logic/deprecatedEl.ts';
import type { FileInfoArr } from '../../data/data-interfaces.external.ts';
import styleText from './as-file-input.css?raw';
import '../base/buttons/as-text-button.ts';
import '../base/buttons/as-button.ts';
import { DROPPED_FILES_EVENT, APP_DROPPED_FILES_EVENT } from './fileDrop.ts';
import { AsFileHandle } from './as-file-handle.ts';
import { registerParamateInput, unregisterParamateInput, liveAppData } from '../../store/store.ts';
import { getFileImportProviderForKind, type FileImportProvider, type FileImportContext, type ImportedFile } from './fileImportProvider.ts';
import '../inputs/as-param-warning.ts';
import { HistoryTrackingMixin } from '../inputs/history-tracking-mixin.ts';

// ---- Source registry ----

// Maps source name (intercepts-dropped attribute value) to the window event it listens for.
// Extend here when new drop origins are introduced.
const DROP_SOURCE_EVENTS: Record<string, string> = {
  viewport: DROPPED_FILES_EVENT,
  app:      APP_DROPPED_FILES_EVENT,
  transfer: 'as-db-mesh-loaded',
};

// ---- Category/accept conflict detection ----

// Representative file extensions per coarse category. Used only to flag an
// obvious category/accept mismatch (e.g. category="mesh" with an image-only
// accept filter). Only mesh and image are validated — text/data are too
// open-ended to check meaningfully.
const CATEGORY_EXTENSIONS: Record<string, readonly string[]> = {
  mesh:  ['stl', 'ply', 'obj', '3mf', 'step', 'stp', 'iges', 'igs', 'gltf', 'glb', 'fbx', '3ds', 'dae', 'off', 'wrl'],
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg', 'tif', 'tiff', 'avif'],
};

// ---- AsFileHandle ----
// Moved to ./as-file-handle.ts (so fileDrop.ts can share it without a cycle);
// imported above for internal use. Re-exported here for backward compatibility
// with existing import paths (`import { AsFileHandle } from '.../as-file-upload'`).
export { AsFileHandle };

// ---- AsFileInput ----

/**
 * Callback registered via onRemoveFile(). Invoked once per removal with the
 * removed handle (the first of the selection when several were loaded — the
 * full set is on the as-file-removed event detail) and the remaining
 * selection. Async callbacks are the expected shape; plain sync functions are
 * accepted too (the return value is promise-wrapped either way). Rejections
 * are logged, not rethrown.
 */
export type RemoveFileCallback =
  (removedFile: AsFileHandle, fileList: readonly AsFileHandle[]) => Promise<void> | void;

// One-time (per page) warning when a listener for the deprecated event name is
// attached. Shared with <as-import-group>, which re-broadcasts the inner events.
let warnedFileSelectedEvent = false;
export function warnFileSelectedListenerDeprecated(hostTag: string): void {
  if (warnedFileSelectedEvent) return;
  warnedFileSelectedEvent = true;
  console.warn(
    `[${hostTag}] the 'as-file-selected' event is deprecated — use 'as-files-selected' (selections only) ` +
    `or 'as-files-change' (any change incl. clears, 1:1 replacement) instead.`
  );
}

/**
 * <as-file-input>
 *
 * (Renamed from <as-file-upload>, which remains registered as a deprecated
 * alias — same behaviour, one-time console warning on first use.)
 *
 * Drop zone + click-to-browse file input. Emits, on the host element:
 *
 * - `as-files-selected` — detail `{ files: AsFileHandle[] }` (never empty).
 *     Fires on a completed user selection, drop, or host-provider import — selections
 *     only, never on clear/remove (removal has its own `as-file-removed` event below).
 *     Bubbles + composed. This is the rich event most consumers want — read
 *     `e.detail.files`, NOT `e.target`.
 * - `as-files-change` — detail `{ files: AsFileHandle[] }` (empty `[]` on clear).
 *     Fires on ANY value change: every selection (alongside `as-files-selected`) and
 *     every clear — the "Remove" button and programmatic clears alike. For consumers
 *     that want one event mirroring the current selection. Bubbles + composed.
 * - `as-file-selected` — DEPRECATED alias of `as-files-change` (one-time console
 *     warning when a listener is attached on this element or an <as-import-group>).
 *     Same firing points and detail; existing listeners keep working unchanged. It was
 *     renamed because "selected" firing on the "Remove" click surprised consumers —
 *     migrate to `as-files-selected` (selections only) or `as-files-change` (1:1).
 * - `change` — no detail. Fires on every value change (select, replace, user "Remove"),
 *     mirroring native form inputs so `onchange` / `@change` / `addEventListener('change')`
 *     works on the host. Read the selection via `e.target.files` (an `AsFileHandle[]`,
 *     see the `files` getter) — NOT `e.target.value`, which is the server upload token and
 *     is empty without a `trcad-param`. NOTE: the inner `<input type=file>`'s native
 *     `change` is `composed:false` and intentionally does NOT cross the shadow boundary;
 *     the host re-emits its own. Programmatic clears (inputs-reset, project-new) are silent.
 *
 * All of the above are dispatched on the element itself (`bubbles: true, composed: true`),
 * not on `window` — listen on the component, or on `document` via bubbling.
 * - `as-file-removed` — detail `{ removedFile, removedFiles, files }`. Fires whenever a
 *     non-empty selection is cleared (the "Remove" button, or any clear() with files loaded —
 *     including programmatic resets). `removedFile` is the first removed handle, `removedFiles`
 *     the full removed set, `files` the remaining selection (currently always `[]`, since
 *     removal clears the whole selection). Bubbles + composed. Alternatively register a
 *     callback via `input.onRemoveFile((removedFile, fileList) => { ... })`.
 * - `as-input-change` — no detail. Undo/history hook (first auto-upload, non-silent clear).
 * - `as-file-uploaded` — detail `{ token, tokens, fileId }`. `auto-upload-file` mode only, on
 *     upload resolve. `tokens` lists every uploaded token (several with `multiple`), `token`
 *     is the first.
 * - `as-data-import` — detail `{ subcategory, fileInfo }`. Analytics, on a successful import.
 *
 * Multiple files: with the `multiple` attribute, the whole selection is uploaded. A trcad
 * `open file f[]` array param (Paramate reports it as type 'array' / elementType 'file')
 * receives the full token array; a scalar `open file f` param takes only the first file,
 * with a console warning from as-configurator. Note trcad rejects an `accept` attribute on
 * `file[]` params, so for array params the accept filter must be set locally on this
 * component. In the GrabCAD env, `multiple` also switches the host's open dialog to
 * multi-select (files convert sequentially). Session restore recovers the full selection:
 * every stored file of a `file[]` param is fetched from the session zip and re-shown.
 */
export class AsFileInput extends HistoryTrackingMixin(HTMLElement) {
  private _labelText!:       HTMLSpanElement;
  private _container!:       HTMLElement;
  private _fileInput!:       HTMLInputElement;
  private _emptyState!:      HTMLElement;
  private _loadedState!:     HTMLElement;
  private _btnBrowse!:       HTMLElement;
  private _btnChangeText!:   HTMLElement;
  private _btnChangeIcon!:   HTMLElement;
  private _dropLabel!:       HTMLSpanElement;
  private _spacer!:          HTMLElement;
  private _acceptsLabel!:    HTMLSpanElement;
  private _fileNameText!:    HTMLSpanElement;
  private _fileSizeText!:    HTMLSpanElement;

  private _isParamMissing: boolean = false;
  private _configurator: any = null;
  private _paramName: string = '';
  private _currentToken: string = '';
  // All tokens of the current selection — one entry for scalar file params,
  // several for `file[]` array params. _currentToken stays the first.
  private _currentTokens: string[] = [];
  private _currentHandle: AsFileHandle | null = null;
  private _currentFiles: AsFileHandle[] = [];
  private _removeFileCbs: RemoveFileCallback[] = [];

  // Signature of the last category/accept conflict warned about, so a repeated
  // _syncAttributes() pass doesn't re-log the same mismatch.
  private _lastCategoryConflictSig: string = '';

  // One-time guard for the deprecated `filetype` attribute warning.
  private _warnedFiletypeDeprecated: boolean = false;

  // Tracks which window event names we're currently subscribed to
  private _listeningTo = new Set<string>();

  get value(): string { return this._currentToken; }

  override addEventListener(type: string, listener: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions): void {
    if (type === 'as-file-selected') warnFileSelectedListenerDeprecated(this.tagName.toLowerCase());
    super.addEventListener(type, listener, options);
  }

  /** Currently-selected file handles (empty when none). Lets stock `change`
   *  handlers read `e.target.files` the way a native <input type=file> exposes
   *  its FileList — here as AsFileHandle[], matching as-files-selected detail.files.
   *  Note: `value` is the server upload token (empty without a trcad-param). */
  get files(): readonly AsFileHandle[] { return this._currentFiles; }

  _getHistoryValue(): any { return this._currentToken; }
  override _historyEntryClearsRedo(): boolean { return !!this._currentToken; }

  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(styleText);
    return s;
  })();

  static get observedAttributes(): string[] {
    return [
      'accept', 'multiple', 'button-text', 'drop-text', 'filetype', 'intercepts-dropped', 'compact',
      'label-default-text', 'trcad-param', 'configure-on-change',
      // no-accepts-label: suppress the "Accepts: ..." hint (label and compact tooltip).
      //   The accept filter itself still applies — this is display-only.
      // minimal-file-list: loaded state shows "N file(s) uploaded" instead of file
      //   names and size.
      'no-accepts-label', 'minimal-file-list',
      // auto-upload-file: upload only — no setParamValue, no configure. Fires 'as-file-uploaded'
      //   with { token, fileId }. First upload (empty→file) is undoable; replace is not.
      // file-id: overrides the fileId sent to configurator.uploadFile(). Default: trcad-param value.
      // delete-on-clear: dispatch 'delete-file' window event when the component is cleared so
      //   consumers can sync trcad state even in auto-upload-file mode.
      'auto-upload-file', 'file-id', 'delete-on-clear',
      // grabcad-stl-unit: unit sent with the GrabCAD convert request for STL files (default 'mm').
      //   Read at click time — only relevant when appEnv === 'grabcad'.
      'grabcad-stl-unit',
      // category: coarse routing hint (mesh | image | text | data, default 'mesh').
      //   Decides whether a host import provider (e.g. GrabCAD's mesh converter)
      //   claims this upload; non-matching kinds use the native file input.
      //   Read at interaction time — orthogonal to accept/filetype filtering, and
      //   distinct from the export-schema FileCategory (file role).
      'category',
    ];
  }

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: 'open' });
    shadow.adoptedStyleSheets = [AsFileInput._sheet];
    shadow.innerHTML = `
      <div class="label-group">
        <span class="label-text" hidden></span>
      </div>
      <div class="container">
        <input type="file" class="file-input">
        <div class="empty-state">
          <img class="upload-icon" src="${assetUrl('assets/img/icons/import-arrow.svg')}" alt="">
          <div class="buttons">
            <as-button class="btn-browse"
              name="choose-file" button-id="choose-file-button" no-default-icon
              default-text="Choose File">
            </as-button>
          </div>
          <span class="drop-label">or drop file here</span>
          <div class="spacer" hidden></div>
          <span class="accepts-label" hidden></span>
        </div>
        <div class="loaded-state" hidden>
          <div class="file-info">
            <span class="file-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
            <div class="file-details">
              <span class="file-name-text"></span>
              <span class="file-size-text"></span>
            </div>
          </div>
          <div class="loaded-actions">
            <as-text-button class="btn-change-text" name="choose-new-file" button-id="choose-new-file-button">Choose new file</as-text-button>
            <as-text-button class="btn-clear" name="remove-file" button-id="remove-file-button" type="danger">Remove</as-text-button>
          </div>
          <as-button class="btn-change-icon" name="choose-new-file" button-id="choose-new-file-icon-button" abs-img-path="${assetUrl('assets/img/icons/import-arrow.svg')}"></as-button>
        </div>
      </div>
    `;
    this._labelText    = shadow.querySelector<HTMLSpanElement>('.label-text')!;
    this._container    = shadow.querySelector<HTMLElement>('.container')!;
    this._fileInput    = shadow.querySelector<HTMLInputElement>('.file-input')!;
    this._emptyState   = shadow.querySelector<HTMLElement>('.empty-state')!;
    this._loadedState  = shadow.querySelector<HTMLElement>('.loaded-state')!;
    this._btnBrowse    = shadow.querySelector<HTMLElement>('.btn-browse')!;
    this._btnChangeText = shadow.querySelector<HTMLElement>('.btn-change-text')!;
    this._btnChangeIcon = shadow.querySelector<HTMLElement>('.btn-change-icon')!;
    this._dropLabel    = shadow.querySelector<HTMLSpanElement>('.drop-label')!;
    this._spacer       = shadow.querySelector<HTMLElement>('.spacer')!;
    this._acceptsLabel = shadow.querySelector<HTMLSpanElement>('.accepts-label')!;
    this._fileNameText = shadow.querySelector<HTMLSpanElement>('.file-name-text')!;
    this._fileSizeText = shadow.querySelector<HTMLSpanElement>('.file-size-text')!;

    this._setupInternalListeners();
  }

  connectedCallback(): void {
    this._syncAttributes();
    this._syncSourceListeners();
    this._syncRestoreListener();
    this._updateParamRegistration();
    this._requestConfigurator();
    window.addEventListener('as-inputs-reset', this.clear as EventListener);
    window.addEventListener('as-restore-param-values', this._handleRestoreParamValues);
    window.addEventListener('as-restore-input-values', this._handleRestoreInputValues);
    window.addEventListener('as-paramate-params-updated', this._handleParamsUpdated);
    this.addEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);
    this._registerHistoryTracking();
  }

  disconnectedCallback(): void {
    this._syncSourceListeners();
    window.removeEventListener('paramate-configurator-after-restore', this._handleAfterRestore);
    window.removeEventListener('as-inputs-reset', this.clear as EventListener);
    window.removeEventListener('as-restore-param-values', this._handleRestoreParamValues);
    window.removeEventListener('as-restore-input-values', this._handleRestoreInputValues);
    window.removeEventListener('as-paramate-params-updated', this._handleParamsUpdated);
    this.removeEventListener('as-paramate-value-resolved', this._handleParamateValueResolved as EventListener);
    this._unregisterHistoryTracking();

    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
    }
  }

  attributeChangedCallback(name: string, _old: string | null, _new: string | null): void {
    switch (name) {
      case 'accept':
      case 'multiple':
      case 'button-text':
      case 'drop-text':
      case 'filetype':
      case 'compact':
      case 'label-default-text':
      case 'configure-on-change':
      case 'category':
      case 'no-accepts-label':
        this._syncAttributes();
        break;
      case 'minimal-file-list':
        this._renderLoadedInfo();
        break;
      case 'intercepts-dropped':
        this._syncSourceListeners();
        break;
      case 'trcad-param':
        this._syncRestoreListener();
        this._updateParamRegistration();
        this._requestConfigurator();
        break;
    }
  }

  private _updateParamRegistration(): void {
    if (this._paramName) {
      unregisterParamateInput(this._paramName, this);
    }
    this._paramName = (this.getAttribute('trcad-param') ?? '').trim();
    if (this._paramName) {
      registerParamateInput(this._paramName, this);
    }
  }

  private _handleProjectNew = () => {
    this.clear({ silenceChange: true });
  };

  /** Register callback(s) invoked when a loaded file is removed (see the
   *  as-file-removed event docs above). Accepts a single callback or an array;
   *  each call replaces the previously registered set. Pass null to
   *  unregister. Callbacks run concurrently and are each promise-wrapped, so
   *  sync and async functions both work. */
  public onRemoveFile(cb: RemoveFileCallback | RemoveFileCallback[] | null): void {
    this._removeFileCbs = cb == null ? [] : Array.isArray(cb) ? [...cb] : [cb];
  }

  /** Clear the current file selection. Set silenceChange:true to skip as-input-change. */
  public clear = (opt?: { silenceChange?: boolean }): void => {
    const silenceChange = opt?.silenceChange ?? true; // Default to silent
    const removedFiles = this._currentFiles;
    this._resetToEmpty();

    const paramName = this.getAttribute('trcad-param');
    const isAutoUpload = this.hasAttribute('auto-upload-file');

    if (paramName && !isAutoUpload) {
      // Normal trcad-param mode: delete server token and reconfigure to empty.
      window.dispatchEvent(new CustomEvent('delete-file', {
        detail: { fileParamName: paramName }
      }));
      window.dispatchEvent(new CustomEvent('as-configure', {
        detail: {
          params: {
            [paramName.startsWith('main:') ? paramName : `main:${paramName}`]: { value: '' }
          },
          skipHistory: silenceChange
        }
      }));
    } else if (this.hasAttribute('delete-on-clear')) {
      // auto-upload-file or standalone: dispatch delete-file so consumers can sync trcad state.
      window.dispatchEvent(new CustomEvent('delete-file', {
        detail: { fileParamName: paramName || this.id }
      }));
    }

    if (!silenceChange) {
      this.dispatchEvent(new CustomEvent('as-input-change', { bubbles: true, composed: true }));
      this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
    }

    // as-files-selected is selection-only and deliberately NOT dispatched on
    // clear; the any-change signal here is as-files-change (empty files), and
    // removal specifically is as-file-removed below. The deprecated
    // as-file-selected keeps its legacy behaviour of also firing on clear —
    // exactly why it was renamed.
    this.dispatchEvent(new CustomEvent('as-files-change', {
      detail: { files: [] },
      bubbles: true,
      composed: true,
    }));
    this.dispatchEvent(new CustomEvent('as-file-selected', {
      detail: { files: [] },
      bubbles: true,
      composed: true,
    }));

    // Removal notification: only when something was actually loaded, so clears
    // of an already-empty input stay silent.
    if (removedFiles.length) {
      const removedFile = removedFiles[0];
      this.dispatchEvent(new CustomEvent('as-file-removed', {
        detail: { removedFile, removedFiles, files: this._currentFiles },
        bubbles: true,
        composed: true,
      }));
      for (const cb of this._removeFileCbs) {
        Promise.resolve()
          .then(() => cb(removedFile, this._currentFiles))
          .catch(err => console.error('[as-file-input] onRemoveFile callback failed:', err));
      }
    }
  };

  private _syncAttributes(): void {
    if (!this._fileInput) return;
    const isCompact = this.hasAttribute('compact');
    this._container.classList.toggle('compact', isCompact);
    this._spacer.hidden = !isCompact;

    const label = this.getAttribute('label-default-text');
    this._labelText.textContent = label ?? '';
    this._labelText.hidden = label === null && !this._isParamMissing;

    // Use the effective filter (incl. any category-derived default) so the OS
    // picker greys out non-matching files consistently with drop validation.
    this._fileInput.accept   = this._effectiveFiletype() || '*/*';
    this._fileInput.multiple = this.hasAttribute('multiple');
    this._btnBrowse.setAttribute('default-text', this.getAttribute('button-text') ?? 'Choose File');
    
    // Warning component
    const labelGroup = this.shadowRoot!.querySelector('.label-group')!;
    let warning = labelGroup.querySelector('as-param-warning');
    if (!warning) {
      warning = document.createElement('as-param-warning');
      labelGroup.appendChild(warning);
    }
    warning.setAttribute('trcad-param', this._paramName);
    warning.setAttribute('is-missing', String(this._isParamMissing));

    const filetype = this._effectiveFiletype();
    if (filetype && !this.hasAttribute('no-accepts-label')) {
      const acceptsText = `Accepts: ${filetype.split(',').map(s => s.trim()).join(', ')}`;
      if (isCompact) {
        this._btnBrowse.setAttribute('tooltip-default-text', acceptsText);
        this._btnBrowse.setAttribute('tooltip-placement', 'bottom');
        this._acceptsLabel.hidden = true;
      } else {
        this._btnBrowse.removeAttribute('tooltip-default-text');
        this._acceptsLabel.textContent = acceptsText;
        this._acceptsLabel.hidden = false;
      }
    } else {
      this._btnBrowse.removeAttribute('tooltip-default-text');
      this._acceptsLabel.hidden = true;
    }

    this._checkCategoryAcceptConflict();
  }

  /** Coarse categories implied by the file types in an accept string. Reads
   *  extensions (".png") and top-level MIME groups ("image/*", "model/stl"). */
  private _acceptCategories(accept: string): Set<string> {
    const cats = new Set<string>();
    for (const raw of accept.split(',')) {
      const tok = raw.trim().toLowerCase();
      if (!tok || tok === '*/*') continue;
      if (tok.includes('/')) {
        const top = tok.split('/')[0];
        if (top === 'image') cats.add('image');
        if (top === 'model') cats.add('mesh');
        continue;
      }
      const ext = tok.startsWith('.') ? tok.slice(1) : tok;
      for (const [cat, exts] of Object.entries(CATEGORY_EXTENSIONS)) {
        if (exts.includes(ext)) cats.add(cat);
      }
    }
    return cats;
  }

  /** Warn when an explicit category="mesh"|"image" disagrees with the accept
   *  filter (e.g. category="mesh" but accept lists image types). The local
   *  category is authoritative and routing is unaffected — this only surfaces a
   *  likely authoring/param mistake. Deduped against the last warned mismatch. */
  private _checkCategoryAcceptConflict(): void {
    const category = (this.getAttribute('category') ?? '').trim().toLowerCase();
    const accept = (this.getAttribute('accept') ?? '').trim();
    const opposite = category === 'mesh' ? 'image' : category === 'image' ? 'mesh' : '';

    let conflict = '';
    if (opposite && accept && accept !== '*/*' && this._acceptCategories(accept).has(opposite)) {
      conflict = `${category}|${this._normalizeAccept(accept)}`;
    }
    if (conflict && conflict !== this._lastCategoryConflictSig) {
      const param = this.getAttribute('trcad-param');
      console.warn(
        `[as-file-input] category="${category}" conflicts with accept="${accept}": ` +
        `the accept filter includes ${opposite} file types` +
        (param ? ` (trcad-param "${param}")` : '') + '.'
      );
    }
    this._lastCategoryConflictSig = conflict;
  }

  private _requestConfigurator(): void {
    const paramName = this.getAttribute('trcad-param');
    if (!paramName) return;

    const handleConfiguratorResponse = (event: CustomEvent<{ configurator: any }>) => {
      this._configurator = event.detail.configurator;
      this.removeEventListener('configurator', handleConfiguratorResponse as EventListener);
      this._checkParamPresence();
    };

    this.addEventListener('configurator', handleConfiguratorResponse as EventListener);
    window.dispatchEvent(new CustomEvent('get-configurator', { detail: { requester: this } }));
  }

  private _checkParamPresence(): void {
    if (!this._configurator?.params) return;
    const paramName = this.getAttribute('trcad-param');
    if (!paramName) return;

    const trimmedName = paramName.trim();
    const fullName = trimmedName.startsWith('main:') ? trimmedName : `main:${trimmedName}`;
    const shortName = trimmedName.startsWith('main:') ? trimmedName.slice(5) : trimmedName;

    const param = this._configurator.params[fullName] ?? this._configurator.params[shortName];
    this._isParamMissing = param === undefined;
    if (param) this._syncAcceptFromParam(param);
    this._syncAttributes();
  }

  /** A trcad file param may carry its own `accept` filter (HTML accept syntax,
   *  e.g. ".stl, .ply"). Adopt it when the host hasn't set an `accept` locally.
   *  If both are present and disagree, the local attribute wins (host intent is
   *  explicit) but we warn so the mismatch is visible.
   *  Note: trcad rejects `accept` on `file[]` array params, so array-linked
   *  inputs never receive one here — set the filter locally instead. */
  private _syncAcceptFromParam(param: any): void {
    const paramAccept = typeof param?.accept === 'string' ? param.accept.trim() : '';
    if (!paramAccept) return;
    const localAccept = (this.getAttribute('accept') ?? '').trim();
    if (!localAccept) {
      this.setAttribute('accept', paramAccept);
      return;
    }
    if (this._normalizeAccept(localAccept) !== this._normalizeAccept(paramAccept)) {
      console.warn(
        `[as-file-input] accept conflict for trcad-param "${this.getAttribute('trcad-param')}": ` +
        `local accept="${localAccept}" overrides param accept="${paramAccept}".`
      );
    }
  }

  /** Order/space/case-insensitive canonical form for comparing accept strings. */
  private _normalizeAccept(s: string): string {
    return s.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).sort().join(',');
  }

  private _handleParamsUpdated = (ev: Event): void => {
    this._checkParamPresence();
  };

  /** Non-empty upload tokens of a file param value: a scalar param holds
   *  '' | token, a file[] param a token array (empty strings when unset). */
  private _tokensOf(value: any): string[] {
    const raw = Array.isArray(value) ? value : [value];
    return raw.filter((v: any) => typeof v === 'string' && v.length > 5);
  }

  /** Whether the given token list matches the current selection's tokens. */
  private _sameTokens(tokens: string[]): boolean {
    const current = this._currentTokens.length
      ? this._currentTokens
      : this._currentToken ? [this._currentToken] : [];
    return tokens.length === current.length && tokens.every((t, i) => t === current[i]);
  }

  /** Reset the selection state and show the empty drop zone. */
  private _resetToEmpty(): void {
    this._currentToken = '';
    this._currentTokens = [];
    this._currentHandle = null;
    this._currentFiles = [];
    this._fileInput.value = '';
    this._emptyState.hidden = false;
    this._loadedState.hidden = true;
    this._fileNameText.textContent = '';
    this._fileSizeText.textContent = '';
  }

  private _handleParamateValueResolved = (ev: CustomEvent): void => {
    const { value } = ev.detail;
    if (value === undefined) return;

    const tokens = this._tokensOf(value);
    const isEmpty = value === '' || value === null
      || (Array.isArray(value) && tokens.length === 0);
    if (isEmpty) {
      // Only clear if we actually have something to clear
      if (this._currentToken || this._currentHandle) this._resetToEmpty();
    } else if (tokens.length) {
      if (this._sameTokens(tokens) && this._currentHandle) return; // Already showing these
      this._currentToken = tokens[0];
      this._currentTokens = tokens;
      const paramName = this.getAttribute('trcad-param');
      if (paramName) this._fetchAndShowSessionFile(paramName);
    }
  };

  private _syncSourceListeners(): void {
    const active = this.isConnected && this.hasAttribute('intercepts-dropped');
    if (!active) {
      for (const evName of this._listeningTo) window.removeEventListener(evName, this._handleSourceDrop);
      this._listeningTo.clear();
      return;
    }
    const val = (this.getAttribute('intercepts-dropped') ?? '').trim();
    const sourceNames = val ? val.split(',').map(s => s.trim()).filter(Boolean) : Object.keys(DROP_SOURCE_EVENTS);
    const wanted = new Set(sourceNames.map(s => DROP_SOURCE_EVENTS[s]).filter(Boolean));
    for (const evName of this._listeningTo) {
      if (!wanted.has(evName)) {
        window.removeEventListener(evName, this._handleSourceDrop);
        this._listeningTo.delete(evName);
      }
    }
    for (const evName of wanted) {
      if (!this._listeningTo.has(evName)) {
        window.addEventListener(evName, this._handleSourceDrop);
        this._listeningTo.add(evName);
      }
    }
  }

  private _setupInternalListeners(): void {
    this._btnBrowse.addEventListener('as-button-click', () => this._openFilePicker());
    this._btnChangeText.addEventListener('click', () => this._openFilePicker());
    this._btnChangeIcon.addEventListener('as-button-click', () => this._openFilePicker());
    // User-initiated Remove fires change + as-input-change; passing the PointerEvent
    // straight to clear() would be read as a (silent) opt, so wrap it explicitly.
    this.shadowRoot!.querySelector('.btn-clear')?.addEventListener('click', () => this.clear({ silenceChange: false }));
    this._fileInput.addEventListener('change', () => {
      const files = this._fileInput.files;
      if (files?.length) this._processFiles(Array.from(files));
    });
    this._container.addEventListener('dragover', (e) => {
      if (e.dataTransfer?.types.includes('Files')) { e.preventDefault(); this._container.classList.add('drag-active'); }
    });
    this._container.addEventListener('dragleave', () => this._container.classList.remove('drag-active'));
    this._container.addEventListener('drop', (e) => {
      e.preventDefault(); e.stopPropagation(); this._container.classList.remove('drag-active');
      const files = e.dataTransfer?.files;
      if (files?.length) this._processFiles(Array.from(files));
    });
  }

  /** Coarse routing kind for host import providers. Defaults to 'mesh' — the
   *  established behaviour, since the only host provider (GrabCAD) converts
   *  meshes. Set category="image" | "text" | "data" to bypass the host
   *  provider and use the native file input. Orthogonal to accept/filetype. */
  private _category(): string {
    return (this.getAttribute('category') ?? 'mesh').trim().toLowerCase();
  }

  /** Host import provider for the current env, but only if it claims this
   *  component's category. Null otherwise (→ native file input). */
  private _importProvider(): FileImportProvider | null {
    return getFileImportProviderForKind(liveAppData.appEnv, this._category());
  }

  /** Open the file picker programmatically, honouring any host import provider
   *  (e.g. GrabCAD's native open/convert dialog) for the current env + category.
   *  Hosts that hide this component and drive it from their own button MUST call
   *  this instead of clicking the inner <input> directly — reaching into the
   *  shadow DOM bypasses provider routing and always opens the plain browser
   *  dialog. */
  public openFilePicker(): void {
    this._openFilePicker();
  }

  private _openFilePicker(): void {
    const provider = this._importProvider();
    if (provider) {
      void this._runProviderFlow(() => provider.pickFile(this._providerContext()));
      return;
    }
    this._fileInput.click();
  }

  // ---- Host import providers (plugin pattern) ----
  // Host environments (e.g. GrabCAD) replace the standard open/drop flow with
  // their own native dialogs and file conversion. Providers self-register in
  // their plugin module (see fileImportProvider.ts) so this component stays
  // host-agnostic. Looked up at interaction time so no env subscription is needed.

  private _providerBusy = false;

  private _providerContext(): FileImportContext {
    return {
      getAttribute: (name) => this.getAttribute(name),
      onProgress: (p) => devlog('[as-file-input] import provider progress:', p),
    };
  }

  /** If the active provider claims the dropped files (e.g. to convert them),
   *  run its flow and return true. Unclaimed drops take the normal flow. */
  private _interceptDropForProvider(blobs: Blob[]): boolean {
    const provider = this._importProvider();
    if (!provider) return false;
    const flow = provider.claimDrop(blobs, this._providerContext());
    if (!flow) return false;
    void this._runProviderFlow(flow);
    return true;
  }

  private async _runProviderFlow(flow: () => Promise<ImportedFile[] | null>): Promise<void> {
    if (this._providerBusy) return; // GrabCAD's protocol has no correlation id — one flow at a time
    this._providerBusy = true;
    try {
      const results = await flow();
      if (!results?.length) return; // dialog dismissed or conversion aborted
      // The provider already filtered and converted the selection, so the
      // results bypass _matchesFiletype.
      const handles = results.map(r =>
        new AsFileHandle(new File([r.data], r.filename), r.filename, r.filetype));
      this._dispatch(handles);
    } catch (err: any) {
      devlog('[as-file-input] import provider failed:', err);
      this.dispatchEvent(new CustomEvent('as-data-import', {
        detail: { subcategory: liveAppData.appEnv, error: err?.message ?? String(err) },
        bubbles: true, composed: true,
      }));
      document.querySelector('as-app')?.dispatchEvent(new CustomEvent('as-notify', {
        detail: { defaultText: `File import failed: ${err?.message ?? err}`, type: 'error', persist: true },
      }));
    } finally {
      this._providerBusy = false;
    }
  }

  private _syncRestoreListener(): void {
    window.removeEventListener('paramate-configurator-after-restore', this._handleAfterRestore);
    if (this.isConnected && this.getAttribute('trcad-param')) window.addEventListener('paramate-configurator-after-restore', this._handleAfterRestore);
  }

  private _handleAfterRestore = () => {
    const paramName = this.getAttribute('trcad-param');
    if (paramName) this._fetchAndShowSessionFile(paramName);
  };

  private _handleRestoreParamValues = (ev: Event) => {
    const { params } = (ev as CustomEvent).detail ?? {};
    const paramName = this.getAttribute('trcad-param');
    if (!paramName || !params) return;
    const fullName = paramName.startsWith('main:') ? paramName : `main:${paramName}`;
    const shortName = paramName.startsWith('main:') ? paramName.slice(5) : paramName;
    const val = params[fullName] ?? params[shortName];
    if (val === undefined) return;
    const tokens = this._tokensOf(val);
    if (!tokens.length) {
      this._resetToEmpty();
    } else if (!this._sameTokens(tokens)) {
      this._currentToken = tokens[0];
      this._currentTokens = tokens;
      this._fetchAndShowSessionFile(paramName);
    }
  };

  /** Handles undo for auto-upload-file mode ('input' history action restoring to null/empty).
   *  Clears the component and deletes the uploaded token from the server. */
  private _handleRestoreInputValues = (ev: Event): void => {
    const { inputs } = (ev as CustomEvent).detail ?? {};
    if (!inputs || !this.id || inputs[this.id] === undefined) return;
    const val = inputs[this.id];
    if (val) return; // redo path — restoring to a token value is not yet supported for auto-upload-file
    const prevToken = this._currentToken;
    this._resetToEmpty();
    // Always delete the server-side file token when undoing an auto-upload.
    if (prevToken) {
      const fileId = this.getAttribute('file-id') || this.getAttribute('trcad-param') || this.id;
      window.dispatchEvent(new CustomEvent('delete-file', { detail: { fileParamName: fileId } }));
      window.dispatchEvent(new CustomEvent('as-history-truncate-redo'));
    }
  };

  private _fetchAndShowSessionFile(paramName: string) {
    window.dispatchEvent(new CustomEvent('as-get-session-file', {
      detail: {
        paramName,
        resolve: (result: any) => {
          if (result) {
            // result.files lists every stored file (several for file[] array
            // params); older resolvers may send only the flat { blob, filename }.
            const stored: Array<{ blob: Blob; filename: string }> =
              Array.isArray(result.files) && result.files.length ? result.files : [result];
            const handles = stored.map(f => new AsFileHandle(f.blob, f.filename));
            this._currentHandle = handles[0];
            this._showLoadedState(handles);
          } else {
            // Only revert if we don't already have a handle (prevent flicker after upload)
            if (!this._currentHandle) {
              this._fileInput.value = '';
              this._emptyState.hidden = false;
              this._loadedState.hidden = true;
            }
          }
        },
        reject: (err: any) => {
          devlog('[as-file-input] session file restore failed:', err);
          if (!this._currentHandle) {
            this._fileInput.value = '';
            this._emptyState.hidden = false;
            this._loadedState.hidden = true;
          }
        },
      },
    }));
  }

  /** Tell the user that one or more selected/dropped files were rejected by the
   *  accept filter, naming them and the accepted types. Routed through as-app's
   *  notification system (same pattern as _runProviderFlow). Without an as-app
   *  host (standalone/demo) it is a silent no-op. */
  private _notifyRejected(rejectedNames: string[]): void {
    if (!rejectedNames.length) return;
    const accepts = this._effectiveFiletype();
    const acceptsText = accepts
      ? ` Accepts: ${accepts.split(',').map(s => s.trim()).join(', ')}.`
      : '';
    const names = rejectedNames.map(n => `"${n}"`).join(', ');
    const msg = rejectedNames.length === 1
      ? `${names} isn't an accepted file type.${acceptsText}`
      : `These files aren't accepted types: ${names}.${acceptsText}`;
    document.querySelector('as-app')?.dispatchEvent(new CustomEvent('as-notify', {
      detail: { defaultText: msg, type: 'error', persist: true },
    }));
  }

  /** Partition items by the accept filter, notifying the user about any rejected
   *  by type. Returns the accepted subset (valid files still proceed; only the
   *  rejected ones are dropped, with a notice). */
  private _partitionByFiletype<T>(items: T[], nameOf: (t: T) => string, typeOf: (t: T) => string): T[] {
    const accepted: T[] = [];
    const rejected: string[] = [];
    for (const it of items) {
      if (this._matchesFiletype(nameOf(it), typeOf(it))) accepted.push(it);
      else rejected.push(nameOf(it));
    }
    this._notifyRejected(rejected);
    return accepted;
  }

  private _handleSourceDrop = (e: Event) => {
    const detail = (e as CustomEvent).detail;
    let raw = detail?.files;
    if (!raw && detail?.stl) raw = [detail.stl];
    if (!raw?.length) return;
    // Sources may send AsFileHandle[] (drop zones), native File/Blob (mesh
    // transfer's detail.stl), or the legacy { filename, filetype, blob } shape.
    // Normalize everything to AsFileHandle so the rest of the path is uniform.
    const handles: AsFileHandle[] = raw.map((f: any) =>
      f instanceof AsFileHandle ? f
      : f instanceof Blob       ? new AsFileHandle(f)
      :                           new AsFileHandle(f.blob, f.filename, f.filetype));
    if (this._interceptDropForProvider(handles.map(h => h.blob))) return;
    const accepted = this._partitionByFiletype(handles, h => h.name, h => h.type);
    if (accepted.length) this._dispatch(accepted);
  };

  setFiles(files: File | File[]): void {
    this._processFiles(Array.isArray(files) ? files : [files]);
  }

  private _processFiles(files: File[]) {
    if (this._interceptDropForProvider(files)) return;
    const accepted = this._partitionByFiletype(files, f => f.name, f => f.type);
    const handles = accepted.map(f => new AsFileHandle(f));
    if (handles.length) this._dispatch(handles);
  }

  private _showLoadedState(handles: AsFileHandle[]) {
    if (!handles.length) return;
    this._currentHandle = handles[0];
    this._currentFiles = handles;
    this._renderLoadedInfo();
    this._emptyState.hidden = true; this._loadedState.hidden = false;
  }

  private _renderLoadedInfo(): void {
    const handles = this._currentFiles;
    if (!handles.length) return;
    if (this.hasAttribute('minimal-file-list')) {
      this._fileNameText.textContent = `${handles.length} file${handles.length === 1 ? '' : 's'} uploaded`;
      this._fileSizeText.textContent = '';
    } else {
      this._fileNameText.textContent = handles.length === 1 ? handles[0].name : `${handles[0].name} +${handles.length - 1} more`;
      this._fileSizeText.textContent = handles.length === 1 ? `${(handles[0].size / 1024).toFixed(1)} KB` : '';
    }
  }

  /** File extension, lowercased — the most reliable mesh-vs-other discriminator (MIME for STL is unreliable). */
  private _ext(h: AsFileHandle): string {
    return h.extension || h.type.toLowerCase();
  }

  /**
   * Fire a unified as-data-import event at the end of a successful import. We don't
   * fire an import-started here: the native file picker gives no cancel signal, so we
   * can't track the full lifecycle. Mesh-vs-file is not distinguished at the event
   * level — the fileInfo array carries each file's type and size. The subcategory is
   * 'grabcad' when a host import provider handled the selection, otherwise 'local'.
   */
  private _dispatchImportDone(handles: AsFileHandle[]): void {
    if (!handles.length) return;
    const fileInfo: FileInfoArr = handles.map(h => ({ sizeBytes: h.size, fileType: this._ext(h) }));
    const env = liveAppData.appEnv;
    // subcategory reflects who actually handled the import: the host env only
    // when its provider claimed this category, otherwise the native 'local' path.
    const subcategory = this._importProvider() ? env : 'local';
    this.dispatchEvent(new CustomEvent('as-data-import', {
      detail: { subcategory, fileInfo }, bubbles: true, composed: true,
    }));
  }

  /** Notify consumers of a selection change: stock `change` (so onchange/@change works
   *  like native inputs) plus the richer `as-files-selected` / `as-files-change`
   *  carrying the handles (and the deprecated `as-file-selected` alias, kept for
   *  existing listeners). */
  private _emitSelected(files: AsFileHandle[]): void {
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('as-files-selected', { detail: { files }, bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('as-files-change', { detail: { files }, bubbles: true, composed: true }));
    this.dispatchEvent(new CustomEvent('as-file-selected', { detail: { files }, bubbles: true, composed: true }));
  }

  private async _dispatch(handles: AsFileHandle[]) {
    const files = this.hasAttribute('multiple') ? handles : [handles[0]];
    if (!files[0]) return;

    // Replacing an existing file is destructive (the old binary at this slot is gone),
    // so confirm BEFORE swapping the UI or emitting any event — cancelling must be a no-op.
    const previousToken = this._currentToken;
    const isReplacement = !!previousToken;
    if (isReplacement) {
      const confirmed = window.confirm('Replacing this file cannot be undone. Continue?');
      if (!confirmed) return;
    }

    this._showLoadedState(files);
    this._emitSelected(files);

    // Multi-selections travel as File[] so the configurator can upload every
    // file (token array for `file[]` params; scalar params take the first
    // with a console warning there).
    const payload: File | File[] = files.length === 1
      ? files[0].toFile()
      : files.map(f => f.toFile());

    if (this.hasAttribute('auto-upload-file')) {
      // Upload-only mode: do not set a param value or trigger configure.
      // First upload (empty → file) is undoable via 'input' history.
      // Replacing an existing file is NOT undoable (old binary is gone from that fileId slot).
      const fileId = this.getAttribute('file-id') || this.getAttribute('trcad-param') || this.id;
      window.dispatchEvent(new CustomEvent('upload-file', {
        detail: {
          file: payload,
          fileParamName: fileId,
          configure: false,
          uploadOnly: true,
          resolve: (token: string, tokens?: string[]) => {
            this._currentToken = token;
            this._currentTokens = tokens ?? [token];
            this.dispatchEvent(new CustomEvent('as-file-uploaded', { detail: { token, tokens: tokens ?? [token], fileId }, bubbles: true, composed: true }));
            // Record undo history only for first upload (empty → file).
            // Replacing is non-undoable because the old binary at fileId is gone.
            if (!previousToken) {
              if (!this.id) { const tag = this.tagName.toLowerCase(); this.id = `${tag}-upload-${Math.random().toString(36).slice(2,7)}`; }
              this.dispatchEvent(new CustomEvent('as-input-change', { bubbles: true, composed: true }));
            }
            this._dispatchImportDone(files);
          },
          reject: (err: any) => console.error(err),
        },
      }));
      return;
    }

    const paramName = this.getAttribute('trcad-param');
    if (paramName) {
      window.dispatchEvent(new CustomEvent('upload-file', {
        detail: {
          file: payload,
          fileParamName: paramName,
          configure: this.hasAttribute('configure-on-change'),
          resolve: (token: string, _parmRes?: any, tokens?: string[]) => {
            this._currentToken = token;
            this._currentTokens = tokens ?? [token];
            if (isReplacement) {
              window.dispatchEvent(new CustomEvent('as-history-clear'));
            }
            this._dispatchImportDone(files);
          },
          reject: (err: any) => console.error(err)
        },
      }));
    } else {
      // No server upload (no trcad-param): the import is complete once shown locally.
      this._dispatchImportDone(files);
    }
  }

  /** Effective file-type filter, single-sourced from `accept`. Its syntax
   *  (extensions, MIME types, and MIME wildcards) is exactly what
   *  _matchesFiletype parses, so one value drives the native picker, drop/pick
   *  validation, and the "Accepts:" label. The legacy `filetype` attribute
   *  still overrides when present, with a one-time deprecation warning. The
   *  all-types accept wildcard counts as "no filter".
   *
   *  Fallback: with no explicit accept/filetype but an explicit `category` of
   *  mesh|image, derive the filter from CATEGORY_EXTENSIONS so the declared kind
   *  is actually enforced (and rejected files get feedback) even outside a host
   *  import provider. Only mesh/image have meaningful whitelists; the implicit
   *  default category does NOT trigger this (returns '' so behaviour is unchanged
   *  for components that never set `category`). */
  private _effectiveFiletype(): string {
    const filetype = (this.getAttribute('filetype') ?? '').trim();
    if (filetype) {
      if (!this._warnedFiletypeDeprecated) {
        this._warnedFiletypeDeprecated = true;
        console.warn(
          `[as-file-input] the 'filetype' attribute is deprecated; use 'accept' (same syntax) instead.`
        );
      }
      return filetype;
    }
    const accept = (this.getAttribute('accept') ?? '').trim();
    if (accept && accept !== '*/*') return accept;

    const category = (this.getAttribute('category') ?? '').trim().toLowerCase();
    if (category === 'mesh' || category === 'image') {
      return CATEGORY_EXTENSIONS[category].map(e => `.${e}`).join(',');
    }
    return '';
  }

  private _matchesFiletype(filename: string, mimeType: string): boolean {
    const filter = this._effectiveFiletype();
    if (!filter) return true;
    const filters = filter.split(',').map(s => s.trim().toLowerCase());
    const ext = (filename.split('.').pop() ?? '').toLowerCase();
    const mime = mimeType.toLowerCase();
    return filters.some(f => f.startsWith('.') ? ext === f.slice(1) : f.endsWith('/*') ? mime.startsWith(f.slice(0, -2) + '/') : f.includes('/') ? mime === f : ext === f);
  }
}

/** @deprecated Use AsFileInput — the class was renamed with the tag. */
export { AsFileInput as AsFileUpload };

if (!customElements.get('as-file-input')) customElements.define('as-file-input', AsFileInput);

if (!customElements.get('as-file-upload')) {
  customElements.define('as-file-upload', DeprecatedEl(AsFileInput, 'as-file-upload', 'as-file-input'));
}
